import { useCommunityContext } from 'providers';
import { useMemo } from 'react';

import { createFilterBuilder } from '@jebel/utils';

import { GRADUATING_YEARS_METRICS_QUERY } from 'features/scorecard/queries';
import { SNACKBAR_ERROR_MESSAGE, SNACKBAR_SUCCESS_MESSAGE } from 'shared/constants';
import { useSearchContext, useSpreadsheetSearch } from 'shared/features/search';
import { useSpreadsheetContext } from 'shared/features/spreadsheet';
import {
  useEditGraduatingYearMutation,
  useDeleteGraduatingYearMutation,
  GraduatingYearsMetricsQuery,
  GraduatingYearsMetricFilter,
} from 'shared/graphql';

export function useGraduatingYearsSpreadsheetInfo() {
  const { filter: yearsInitialFilter, queryParams } = useSpreadsheetContext();
  const { selectedCommunity: school } = useCommunityContext();
  const { searchQuery } = useSearchContext();

  const yearsFilter = useMemo(() => {
    const filter = createFilterBuilder<GraduatingYearsMetricFilter>(yearsInitialFilter.query);
    const number = Number(searchQuery);

    if (searchQuery && !isNaN(number)) {
      filter.or({ year: { equals: String(number) } });
      filter.or({ expectedUsersCount: { equals: number } });
      filter.or({ missingUsersCount: { equals: number } });
    }

    if (school) {
      // Filter by current school.
      // https://github.com/jebelapp/jebel/issues/1684
      filter.and({ school: { equals: school.id } });
    }

    return filter.build();
  }, [yearsInitialFilter, searchQuery]);

  const { tableData, tableLoading, queryVariables } =
    useSpreadsheetSearch<GraduatingYearsMetricsQuery>({
      query: GRADUATING_YEARS_METRICS_QUERY,
      searchingFields: [],
      queryVariables: {
        ...queryParams,
        filter: yearsFilter,
      },
    });

  const [editGraduatingYear, { loading: editing }] = useEditGraduatingYearMutation({
    context: {
      [SNACKBAR_SUCCESS_MESSAGE]: 'Success! The class has been edited successfully',
      [SNACKBAR_ERROR_MESSAGE]: `Error while editing class!`,
    },
    refetchQueries: ['GraduatingYearsMetrics'],
  });

  const [deleteGraduatingYear, { loading: deleting }] = useDeleteGraduatingYearMutation({
    context: {
      [SNACKBAR_SUCCESS_MESSAGE]: 'Success! The class has been deleted',
      [SNACKBAR_ERROR_MESSAGE]: `Error while deleting class!`,
    },
    refetchQueries: ['GraduatingYearsMetrics'],
  });

  return {
    tableData,
    tableLoading,
    editGraduatingYear,
    editing,
    deleteGraduatingYear,
    deleting,
    queryVariables,
  };
}
